import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  ColDefBuilder,
  DataTable,
  Heading,
  Icon,
  Stack,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const getModelPromptTemplateDetailsQuery = graphql(`
  query getModelPromptTemplateDetails($id: uuid!) {
    llmModelPromptTemplatesByPk(id: $id) {
      __typename
      id
      llm_outputs {
        __typename
        id
        feedback {
          __typename
          id
          rating
          type
          comment
          createdAt
          summaryFeedbackTags {
            __typename
            feedbackTagId
            feedbackTag {
              __typename
              id
              name
            }
          }
        }
      }
      model_args {
        __typename
        id
        modelType
        name
        args
      }
    }
  }
`);

type ModelPromptTemplateFeedbackRow = {
  type: string;
  rating: number | null;
  comment: string | null;
  tags: string[];
  createdAt: string;
};

const columns = new ColDefBuilder<ModelPromptTemplateFeedbackRow>()
  .colDef({
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) =>
      match(row.original.type)
        .with('POSITIVE', () => (
          <Icon name="ThumbsUp" size="xl" className="text-text-positive" />
        ))
        .with('NEGATIVE', () => (
          <Icon name="ThumbsDown" size="xl" className="text-text-negative" />
        ))
        .otherwise(() => '--'),
  })
  .defaultSortable('rating')
  .defaultSortable('comment')
  .dateSortable('createdAt', 'Created At')
  .build();

export const ModelPromptTemplateDetailsPage: React.FC = () => {
  const { modelPromptTemplateId } = useParams() as {
    modelPromptTemplateId: string;
  };

  const {
    data: { llmModelPromptTemplatesByPk: modelPromptTemplate },
  } = useSuspenseQuery(getModelPromptTemplateDetailsQuery, {
    variables: {
      id: modelPromptTemplateId,
    },
  });

  const rows = useMemo(() => {
    const feedbacks = (modelPromptTemplate?.llm_outputs ?? []).map((output) => {
      const { feedback } = output;

      return feedback.map<ModelPromptTemplateFeedbackRow>(
        ({ rating, comment, type, createdAt, summaryFeedbackTags }) => {
          return {
            comment,
            rating,
            type: type!,
            createdAt,
            tags: summaryFeedbackTags.map((sft) => sft.feedbackTag.name),
          };
        },
      );
    });

    return feedbacks.flat();
  }, [modelPromptTemplate]);

  return (
    <>
      <Stack justify="between" gap={2} direction="horizontal">
        <div className="my-2 py-2">
          <Heading>{modelPromptTemplate?.model_args?.modelType}</Heading>
          <pre>{JSON.stringify(modelPromptTemplate?.model_args?.args)}</pre>
        </div>
      </Stack>
      <Heading size="2xl" weight="semibold">
        Feedback
      </Heading>
      <div className="rounded-md bg-white p-2">
        <DataTable columns={columns} data={rows} />
      </div>
    </>
  );
};
